// pages/dashboard/FeedbackResponses.jsx
// Shows all responses for a feedback form
// Table view with all answers
// CSV export

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const FeedbackResponses = () => {
  const { formId } = useParams();
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [responses, setResponses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResponses();
  }, [formId]);

  const fetchResponses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/feedback/responses/${formId}`);
      if (response.data.success) {
        setForm(response.data.data.form);
        setResponses(response.data.data.responses);
        setTotal(response.data.data.total);
      }
    } catch (err) {
      console.error('Fetch responses error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!form || responses.length === 0) return;

    const questions = form.questions?.filter(q => q.active) || [];
    const headers = ['Respondent Name', 'Respondent Email', 'Submitted At', ...questions.map(q => q.questionText)];

    const rows = responses.map(r => {
      const answerMap = {};
      r.answers?.forEach(a => { answerMap[a.questionId] = a.answer; });

      return [
        r.respondentName || 'Anonymous',
        r.respondentEmail || '',
        new Date(r.createdAt).toLocaleString(),
        ...questions.map(q => {
          const ans = answerMap[q.questionId || q._id];
          if (Array.isArray(ans)) return ans.join(', ');
          return ans || '';
        }),
      ];
    });

    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${form.name}_responses_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handleLogout = async () => { await logout(); navigate('/admin/login'); };

  const navItems = [
    { label: 'Dashboard', path: '/admin/dashboard', icon: '📊' },
    { label: 'Submissions', path: '/admin/submissions', icon: '📋' },
    { label: 'Transactions', path: '/admin/transactions', icon: '💳' },
    { label: 'Meetings', path: '/admin/meetings', icon: '📅' },
    { label: 'Enterprise', path: '/admin/enterprise', icon: '🏢' },
    { label: 'Form Maker', path: '/admin/forms', icon: '📝', active: true },
    { label: 'Analytics', path: '/admin/analytics', icon: '📈' },
    { label: 'Settings', path: '/admin/settings', icon: '⚙️' },
  ];

  const activeQuestions = form?.questions?.filter(q => q.active) || [];

  return (
    <div style={s.page}>
      <div style={s.sidebar}>
        <div style={s.sidebarLogo}>
          <span style={s.logoExec}>EXECUTIVE</span>
          <span style={s.logoSpring}>Springboard</span>
        </div>
        <nav style={s.nav}>
          {navItems.map(item => (
            <button key={item.path} style={{ ...s.navItem, ...(item.active ? s.navActive : {}) }} onClick={() => navigate(item.path)}>
              <span>{item.icon}</span><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div style={s.sidebarFooter}>
          <div style={s.adminName}>{admin?.name}</div>
          <button style={s.logoutBtn} onClick={handleLogout}>Sign Out</button>
        </div>
      </div>

      <div style={s.main}>
        <button style={s.backBtn} onClick={() => navigate('/admin/forms')}>← Back to Forms</button>

        <div style={s.header}>
          <div>
            <h1 style={s.title}>{form?.name || 'Responses'}</h1>
            <p style={s.subtitle}>
              💬 Feedback Form · {total} response{total !== 1 ? 's' : ''}
            </p>
          </div>
          <button style={s.btnExport} onClick={handleExportCSV} disabled={responses.length === 0}>
            Export CSV
          </button>
        </div>

        {loading ? (
          <p style={s.loadingText}>Loading responses...</p>
        ) : responses.length === 0 ? (
          <div style={s.empty}>
            <div style={s.emptyIcon}>💬</div>
            <p style={s.emptyTitle}>No responses yet</p>
            <p style={s.emptyText}>Responses will appear here when people submit this form.</p>
          </div>
        ) : (
          <div style={s.tableWrap}>
            <div style={s.tableScroll}>
              {/* Header */}
              <div style={{
                ...s.tableHead,
                gridTemplateColumns: `180px 180px 140px ${activeQuestions.map(() => '200px').join(' ')}`,
              }}>
                <span>Name</span>
                <span>Email</span>
                <span>Submitted</span>
                {activeQuestions.map(q => (
                  <span key={q._id} style={s.qHeader}>{q.questionText}</span>
                ))}
              </div>

              {/* Rows */}
              {responses.map(response => {
                const answerMap = {};
                response.answers?.forEach(a => { answerMap[a.questionId] = a.answer; });

                return (
                  <div
                    key={response._id}
                    style={{
                      ...s.tableRow,
                      gridTemplateColumns: `180px 180px 140px ${activeQuestions.map(() => '200px').join(' ')}`,
                    }}
                  >
                    <span style={s.name}>{response.respondentName || 'Anonymous'}</span>
                    <span style={s.cell}>{response.respondentEmail || 'N/A'}</span>
                    <span style={s.cell}>{new Date(response.createdAt).toLocaleDateString()}</span>
                    {activeQuestions.map(q => {
                      const ans = answerMap[q.questionId || q._id];
                      const display = Array.isArray(ans) ? ans.join(', ') : (ans?.toString() || '');
                      return (
                        <span key={q._id} style={s.answerCell} title={display}>
                          {display || <span style={{ color: 'var(--color-light-gray)' }}>No answer</span>}
                        </span>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: 'var(--font-primary)' },
  sidebar: { width: '240px', backgroundColor: 'var(--color-primary)', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarLogo: { display: 'flex', flexDirection: 'column', gap: '2px', padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#fff', lineHeight: 1 },
  nav: { flex: 1, padding: '16px 0' },
  navItem: { width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '14px', cursor: 'pointer', textAlign: 'left' },
  navActive: { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
  sidebarFooter: { padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' },
  adminName: { fontSize: '13px', fontWeight: '600', color: '#fff', marginBottom: '10px' },
  logoutBtn: { width: '100%', padding: '8px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'rgba(255,255,255,0.5)', fontSize: '12px', cursor: 'pointer' },
  main: { flex: 1, backgroundColor: 'var(--color-dashboard-bg)', padding: '32px', overflowY: 'auto' },
  backBtn: { background: 'none', border: 'none', color: 'var(--color-teal)', fontSize: '14px', cursor: 'pointer', marginBottom: '20px', fontWeight: '500', padding: 0, display: 'block' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '26px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '4px' },
  subtitle: { fontSize: '14px', color: 'var(--color-text-light)' },
  btnExport: { backgroundColor: 'var(--color-off-white)', color: 'var(--color-text)', padding: '10px 20px', borderRadius: '8px', border: '1px solid var(--color-border)', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  loadingText: { padding: '40px', textAlign: 'center', color: 'var(--color-text-light)' },
  empty: { padding: '60px 20px', textAlign: 'center', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)' },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyTitle: { fontSize: '18px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '8px' },
  emptyText: { fontSize: '14px', color: 'var(--color-text-light)' },
  tableWrap: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)', overflow: 'hidden' },
  tableScroll: { overflowX: 'auto' },
  tableHead: { display: 'grid', gap: '0', padding: '10px 16px', fontSize: '11px', fontWeight: '600', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '2px solid var(--color-border)', backgroundColor: 'var(--color-off-white)', minWidth: 'max-content' },
  tableRow: { display: 'grid', gap: '0', padding: '12px 16px', borderBottom: '1px solid var(--color-border)', alignItems: 'start', minWidth: 'max-content' },
  name: { fontSize: '13px', fontWeight: '600', color: 'var(--color-primary)', paddingRight: '16px' },
  cell: { fontSize: '13px', color: 'var(--color-text-light)', paddingRight: '16px' },
  qHeader: { fontSize: '11px', paddingRight: '16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  answerCell: { fontSize: '13px', color: 'var(--color-text)', paddingRight: '16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' },
};

export default FeedbackResponses;
